import axios from 'axios'
import api from './axiosApi'
import type {
  ImageRead,
  PaginatedImagesResponse,
  ImageUploadRequest,
  ImageUploadResponse,
  BulkImageUploadRequest,
  BulkImageUploadResponse,
  ImagePatchRequest,
  LineItemImageRead,
  AssignImageRequest,
  ImageListParams,
  ReorderImageRequest,
} from '@/types/image'

// ─── Global Image Management ────────────────────────────────────

export async function listImages(
  params: ImageListParams
): Promise<PaginatedImagesResponse> {
  const response = await api.get<PaginatedImagesResponse>('/images', {
    params: {
      search: params.search || undefined,
      status: params.status || undefined,
      skip: params.skip ?? 0,
      limit: params.limit ?? 20,
    },
  })
  return response.data
}

export async function getImage(imageId: string): Promise<ImageRead> {
  const response = await api.get<ImageRead>(`/images/${imageId}`)
  return response.data
}

export async function getImageUploadUrl(
  data: ImageUploadRequest
): Promise<ImageUploadResponse> {
  const response = await api.post<ImageUploadResponse>(
    '/images/upload-url',
    data
  )
  return response.data
}

export async function getBulkImageUploadUrls(
  data: BulkImageUploadRequest
): Promise<BulkImageUploadResponse> {
  const response = await api.post<BulkImageUploadResponse>(
    '/images/bulk-upload-url',
    data
  )
  return response.data
}

export async function uploadToPresignedUrl(
  uploadUrl: string,
  file: File
): Promise<void> {
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': file.type,
      'x-ms-blob-type': 'BlockBlob',
    },
  })
}

export async function confirmImageUpload(
  imageId: string,
  sizeBytes?: number
): Promise<ImageRead> {
  const response = await api.post<ImageRead>(
    `/images/${imageId}/confirmations`,
    { size_bytes: sizeBytes ?? null }
  )
  return response.data
}

export async function updateImage(
  imageId: string,
  data: ImagePatchRequest
): Promise<ImageRead> {
  const response = await api.patch<ImageRead>(`/images/${imageId}`, data)
  return response.data
}

export async function deleteImage(imageId: string): Promise<void> {
  await api.delete(`/images/${imageId}`)
}

// ─── Line Item Images ───────────────────────────────────────────

export async function listLineItemImages(
  planId: string,
  versionId: string,
  lineId: string
): Promise<LineItemImageRead[]> {
  const response = await api.get<LineItemImageRead[]>(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/images`
  )
  return response.data
}

export async function assignImagesToLineItem(
  planId: string,
  versionId: string,
  lineId: string,
  imageIds: string[]
): Promise<LineItemImageRead[]> {
  const response = await api.post<LineItemImageRead[]>(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/images`,
    { image_ids: imageIds } satisfies AssignImageRequest
  )
  return response.data
}

export async function unassignImageFromLineItem(
  planId: string,
  versionId: string,
  lineId: string,
  imageId: string
): Promise<void> {
  await api.delete(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/images/${imageId}`
  )
}

export async function setDefaultLineItemImage(
  planId: string,
  versionId: string,
  lineId: string,
  imageId: string
): Promise<void> {
  await api.post(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/images/${imageId}/default`
  )
}

export async function unsetDefaultLineItemImage(
  planId: string,
  versionId: string,
  lineId: string,
  imageId: string
): Promise<void> {
  await api.delete(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/images/${imageId}/default`
  )
}

export async function reorderLineItemImage(
  planId: string,
  versionId: string,
  lineId: string,
  imageId: string,
  data: ReorderImageRequest
): Promise<LineItemImageRead> {
  const response = await api.post<LineItemImageRead>(
    `/plans/${planId}/versions/${versionId}/lines/${lineId}/images/${imageId}/reorder`,
    data
  )
  return response.data
}

// ─── Orchestrator: Upload a single image (3-step flow) ──────────

export async function uploadSingleImage(
  file: File,
  name: string,
  description?: string | null
): Promise<ImageRead> {
  const { image_id, upload_url } = await getImageUploadUrl({
    original_filename: file.name,
    content_type: file.type || 'image/jpeg',
    name,
    description,
  })

  await uploadToPresignedUrl(upload_url, file)

  return confirmImageUpload(image_id, file.size)
}
